import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import baseUrl from "../../url/baseUrl";
import CircleLoader from "react-spinners/CircleLoader";
import "./NewsCarousel.css";

export default function NewsCarousel() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);

  const trackRef = useRef(null);
  const autoTimerRef = useRef(null);
  const resumeTimerRef = useRef(null);
  const isAutoRef = useRef(true); // đang auto-slide?
  const animRef = useRef(null); // animation rAF đang chạy?

  // Easing cuộn mượt
  const smoothScroll = useCallback((el, delta, duration = 500) => {
    if (!el) return;
    // huỷ anim cũ nếu có
    if (animRef.current) cancelAnimationFrame(animRef.current);

    const start = el.scrollLeft;
    const end = start + delta;
    const startTime = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const step = (now) => {
      const p = Math.min(1, (now - startTime) / duration);
      const val = start + (end - start) * ease(p);
      el.scrollLeft = val;
      if (p < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        animRef.current = null;
        // snap về card gần nhất
        snapNearest();
      }
    };
    animRef.current = requestAnimationFrame(step);
  }, []);

  // Tính kích thước 1 card + gap
  const getCardStep = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 0;
    const firstCard = track.querySelector(".news-card");
    if (!firstCard) return 0;
    const w = firstCard.getBoundingClientRect().width;
    const gap = parseFloat(
      getComputedStyle(track).columnGap || getComputedStyle(track).gap || "0"
    );
    return w + gap;
  }, []);

  // Cuộn theo số card (-1 hoặc +1)
  const scrollByCards = useCallback(
    (dir) => {
      const track = trackRef.current;
      if (!track) return;
      const step = getCardStep();
      if (!step) return;
      smoothScroll(track, dir * step, 500);
      // người dùng đã tương tác → tạm dừng auto và hẹn chạy lại
      pauseAuto(6000);
    },
    [getCardStep, smoothScroll]
  );

  // Snap về vị trí card gần nhất (khi thả tay/scroll xong)
  const snapNearest = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const step = getCardStep();
    if (!step) return;
    const left = track.scrollLeft;
    const targetIndex = Math.round(left / step);
    const target = targetIndex * step;
    track.scrollTo({ left: target, behavior: "smooth" });
  }, [getCardStep]);

  // Auto-slide mỗi 5s
  const startAuto = useCallback(() => {
    clearInterval(autoTimerRef.current);
    isAutoRef.current = true;
    autoTimerRef.current = setInterval(() => {
      if (!trackRef.current) return;
      const step = getCardStep();
      if (!step) return;
      // nếu gần cuối -> vẫn cuộn, người dùng có thể kéo ngược
      smoothScroll(trackRef.current, step, 600);
    }, 5000);
  }, [getCardStep, smoothScroll]);

  const stopAuto = useCallback(() => {
    isAutoRef.current = false;
    clearInterval(autoTimerRef.current);
  }, []);

  // Tạm dừng auto và tự khởi động lại sau delay ms
  const pauseAuto = useCallback(
    (delay = 6000) => {
      stopAuto();
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = setTimeout(() => {
        startAuto();
      }, delay);
    },
    [startAuto, stopAuto]
  );

  // Khởi động auto on mount
  useEffect(() => {
    startAuto();
    return () => {
      clearInterval(autoTimerRef.current);
      clearTimeout(resumeTimerRef.current);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [startAuto]);

  // Pause khi người dùng tương tác: hover, touch, wheel, key, scroll
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const pause = () => pauseAuto(6000);
    const onScrollEnd = () => {
      // debounce snapNearest khi người dùng kéo tay
      if (animRef.current) return;
      clearTimeout(onScrollEnd._t);
      onScrollEnd._t = setTimeout(snapNearest, 120);
    };

    track.addEventListener("wheel", pause, { passive: true });
    track.addEventListener("touchstart", pause, { passive: true });
    track.addEventListener("mousedown", pause);
    track.addEventListener("mousemove", pause);
    track.addEventListener("keydown", pause);
    track.addEventListener("scroll", onScrollEnd, { passive: true });

    return () => {
      track.removeEventListener("wheel", pause);
      track.removeEventListener("touchstart", pause);
      track.removeEventListener("mousedown", pause);
      track.removeEventListener("mousemove", pause);
      track.removeEventListener("keydown", pause);
      track.removeEventListener("scroll", onScrollEnd);
      clearTimeout(onScrollEnd._t);
    };
  }, [pauseAuto, snapNearest]);

  // get news from server
  useEffect(() => {
    setLoading(true);
    async function fetchNewsData() {
      try {
        const response = await fetch(baseUrl + "/home-news-rumi");

        if (response.status !== 200) {
          throw new Error("Something went wrong.");
        }

        const newsList = await response.json();
        setNews(newsList);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.log(err);
      }
    }

    fetchNewsData();
  }, []);

  return (
    <section className="news-wrap">
      <h2 className="news-title">TIN TỨC & SỰ KIỆN</h2>
      <p className="news-sub">Cập nhật hoạt động mới nhất từ Rumi</p>

      {loading && (
        <div className="d-flex justify-content-center align-items-center">
          <CircleLoader color="#36d7b7" size={50} />
        </div>
      )}

      {news.length !== 0 && (
        <div className="news-carousel">
          <button
            className="news-nav news-prev"
            aria-label="Prev"
            onClick={() => scrollByCards(-1)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className="news-viewport">
            <div className="news-track" ref={trackRef}>
              {news.map((item) => (
                <article
                  className={`news-card tone-${item.tone}`}
                  key={item._id}
                >
                  <Link className="news-media" to={`/news/${item._id}`}>
                    <img
                      src={item.imageAvatar}
                      alt={item.title}
                      loading="lazy"
                    />
                    {item.category && (
                      <span className="news-chip">{item.category}</span>
                    )}
                  </Link>

                  <div className="news-meta">
                    <h3 className="news-heading">{item.title}</h3>
                    <p className="news-excerpt">{item.shortDesc}</p>
                    <Link
                      className="news-btn"
                      to={`/news/${item._id}`}
                      aria-label={`Xem: ${item.title}`}
                    >
                      Xem chi tiết
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12h14"></path>
                        <path d="M12 5l7 7-7 7"></path>
                      </svg>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <button
            className="news-nav news-next"
            aria-label="Next"
            onClick={() => scrollByCards(1)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}
